import {
  FEEDBACK_SCREENSHOT_CONTENT_TYPES,
  FEEDBACK_SCREENSHOT_MAX_COUNT,
  type ConnectAccountStatus,
  type FeedbackDiagnostics,
  type FeedbackDraft,
  type FeedbackOrigin,
  type FeedbackScreenshot,
  type FeedbackSubmitResult,
  type ReportedFailure,
} from "@afar-research/contracts";

import { isHeicImageFile } from "../../lib/imageCompression";

/** A Feedback Screenshot the form holds, keyed so it can be removed. */
export interface FeedbackFormScreenshot extends FeedbackScreenshot {
  readonly id: string;
}

export interface FeedbackFormState {
  readonly category: string;
  readonly summary: string;
  readonly details: string;
  readonly screenshots: readonly FeedbackFormScreenshot[];
  /** Set when the report was started from a failure; spontaneous reports carry none. */
  readonly origin?: FeedbackOrigin;
  /** "Include where diagnostics are stored", ticked by default (ADR-0138). */
  readonly includeDiagnostics: boolean;
  /** When the report began: the failure's time, or the draft's first change. ISO 8601. */
  readonly startedAt?: string;
  /** The thread a reported failure concerns, when it is not the one on screen. */
  readonly threadRef?: { readonly environmentId: string; readonly threadId: string };
}

export const EMPTY_FEEDBACK_FORM: FeedbackFormState = {
  category: "",
  summary: "",
  details: "",
  screenshots: [],
  includeDiagnostics: true,
};

/** What is sent: the form, and the Diagnostics Pointer only while its box is ticked. */
export const feedbackDraftFromForm = (
  form: FeedbackFormState,
  diagnostics: FeedbackDiagnostics | null,
): FeedbackDraft => ({
  category: form.category,
  summary: form.summary,
  details: form.details,
  screenshots: form.screenshots.map(({ contentType, data }) => ({ contentType, data })),
  ...(form.origin ? { origin: form.origin } : {}),
  ...(form.includeDiagnostics && diagnostics ? { diagnostics } : {}),
});

/** At four bytes a character this stays under half the Details cap, leaving room to write. */
const FEEDBACK_PREFILL_MESSAGE_MAX_LENGTH = 1_000;

/**
 * Starts a bug report from a failure, or leaves the draft alone when the user has already begun
 * one: what someone typed is worth more than a prefill (ADR-0138).
 */
export function prefillFeedbackDraft(
  form: FeedbackFormState,
  failure: ReportedFailure,
): FeedbackFormState {
  if (isFeedbackDraftStarted(form)) return form;
  return {
    ...form,
    category: "Bug",
    summary: failure.summary,
    // Bounded so a long stack still leaves the user room within the Details cap.
    details: `What AFAR showed:\n${failure.message.slice(0, FEEDBACK_PREFILL_MESSAGE_MAX_LENGTH)}\n\n`,
    origin: failure.origin,
    ...(failure.occurredAt ? { startedAt: failure.occurredAt } : {}),
    ...(failure.threadRef ? { threadRef: failure.threadRef } : {}),
  };
}

const isFeedbackDraftStarted = (form: FeedbackFormState) =>
  form.category !== "" ||
  form.summary.trim() !== "" ||
  form.details.trim() !== "" ||
  form.screenshots.length > 0;

/**
 * The draft after any change: dated from its first change, and stripped of where it came from
 * once the user has emptied it, because what they write next is a different report.
 */
export function settleFeedbackDraft(form: FeedbackFormState, now: Date): FeedbackFormState {
  if (!isFeedbackDraftStarted(form))
    return form.origin || form.startedAt || form.threadRef
      ? { ...EMPTY_FEEDBACK_FORM, includeDiagnostics: form.includeDiagnostics }
      : form;
  return form.startedAt ? form : { ...form, startedAt: now.toISOString() };
}

/** Writes the user's home directory as `~`, so a Diagnostics Pointer names no account. */
export function homeRelativePath(path: string): string {
  return path
    .replace(/^\/root(?=\/|$)/, "~")
    .replace(/^\/(?:Users|home)\/[^/]+(?=\/|$)/, "~")
    .replace(/^[A-Za-z]:[\\/]Users[\\/][^\\/]+(?=[\\/]|$)/, "~");
}

/** The facts about one Environment that a Diagnostics Pointer names. */
export interface DiagnosticsEnvironmentFacts {
  readonly label: string;
  readonly connection: "this machine" | "remote" | "SSH" | "paired" | "Cloud Workspace";
  readonly serverVersion: string;
  readonly platform: string;
  /** Absolute, as the Environment reports it; written home-relative here. */
  readonly diagnosticsDirectory?: string;
  readonly provider?: { readonly name: string; readonly version?: string; readonly status: string };
  /** Present for a Cloud Workspace, whose storage location only Connect can name. */
  readonly cloudWorkspaceId?: string;
}

/** What `diagnosticsEnvironmentFacts` reads from one Environment, as plain data. */
export interface DiagnosticsEnvironmentInput {
  readonly environmentId: string;
  readonly label: string;
  readonly targetTag:
    | "PrimaryConnectionTarget"
    | "RemoteConnectionTarget"
    | "SshConnectionTarget"
    | "PairedConnectionTarget";
  readonly sharesClientMachine: boolean;
  /** The Environment runs as a Cloud Workspace, whose providers AFAR manages. */
  readonly managedByAfar: boolean;
  readonly config: {
    readonly environment: {
      readonly serverVersion: string;
      readonly platform: { readonly os: string; readonly arch: string };
    };
    readonly observability: { readonly logsDirectoryPath: string };
    readonly providers: ReadonlyArray<{
      readonly instanceId: string;
      readonly driver: string;
      readonly version: string | null;
      readonly status: string;
    }>;
  };
  /** The provider instance of the thread being reported, when there is one. */
  readonly providerInstanceId?: string | undefined;
}

function diagnosticsConnectionKind(
  input: DiagnosticsEnvironmentInput,
): DiagnosticsEnvironmentFacts["connection"] {
  if (input.managedByAfar) return "Cloud Workspace";
  switch (input.targetTag) {
    case "PrimaryConnectionTarget":
      return input.sharesClientMachine ? "this machine" : "remote";
    case "SshConnectionTarget":
      return "SSH";
    case "PairedConnectionTarget":
      return "paired";
    case "RemoteConnectionTarget":
      return "remote";
  }
}

/** One Environment, as the Diagnostics Pointer names it. */
export function diagnosticsEnvironmentFacts(
  input: DiagnosticsEnvironmentInput,
): DiagnosticsEnvironmentFacts {
  const connection = diagnosticsConnectionKind(input);
  const provider = input.providerInstanceId
    ? input.config.providers.find((candidate) => candidate.instanceId === input.providerInstanceId)
    : undefined;
  return {
    label: input.label,
    connection,
    serverVersion: input.config.environment.serverVersion,
    platform: `${input.config.environment.platform.os}-${input.config.environment.platform.arch}`,
    diagnosticsDirectory: input.config.observability.logsDirectoryPath,
    ...(provider
      ? {
          provider: {
            name: provider.driver,
            ...(provider.version ? { version: provider.version } : {}),
            status: provider.status,
          },
        }
      : {}),
    // A Cloud Workspace's Environment is named by its workspace id (ADR-0138).
    ...(connection === "Cloud Workspace" ? { cloudWorkspaceId: input.environmentId } : {}),
  };
}

/**
 * The Diagnostics Pointer's text: which Environments, which service, where their diagnostics are,
 * and when. Shown in full before sending, and sent exactly as shown.
 */
export function describeDiagnosticsPointer(
  environments: readonly DiagnosticsEnvironmentFacts[],
  reportedAt: Date,
): string {
  return [
    ...environments.flatMap((environment) => [
      `Environment "${environment.label}" (${
        environment.cloudWorkspaceId
          ? `Cloud Workspace ${environment.cloudWorkspaceId}`
          : environment.connection
      })`,
      `  ${environment.cloudWorkspaceId ? "Cloud Workspace version" : "Environment Service"} ${environment.serverVersion} · ${environment.platform}`,
      environment.cloudWorkspaceId
        ? "  Diagnostics: on this Cloud Workspace's storage (AFAR Connect adds the location)"
        : environment.diagnosticsDirectory
          ? `  Diagnostics: ${homeRelativePath(environment.diagnosticsDirectory)}`
          : "  Diagnostics: location not reported",
      ...(environment.provider
        ? [
            `  Provider: ${environment.provider.name}${
              environment.provider.version ? ` ${environment.provider.version}` : ""
            } (${environment.provider.status})`,
          ]
        : []),
    ]),
    `Reported at: ${reportedAt.toISOString()}`,
  ].join("\n");
}

/** What the file picker offers: the sendable types, plus HEIC, which the form converts. */
export const FEEDBACK_SCREENSHOT_FILE_ACCEPT = [
  ...FEEDBACK_SCREENSHOT_CONTENT_TYPES,
  "image/heic",
  "image/heif",
  ".heic",
  ".heif",
].join(",");

/**
 * Which pasted, dropped, or picked files the form takes as screenshots, and what to say about the
 * rest. Images are taken in order until the form holds three; anything else is left out with a
 * sentence rather than silently ignored. HEIC is taken because the form converts it.
 */
export function admitFeedbackScreenshotFiles<Candidate extends { name: string; type: string }>(
  existingCount: number,
  files: readonly Candidate[],
): { readonly accepted: Candidate[]; readonly notice: string | null } {
  const images = files.filter((file) => file.type.startsWith("image/") || isHeicImageFile(file));
  const room = Math.max(0, FEEDBACK_SCREENSHOT_MAX_COUNT - existingCount);
  const accepted = images.slice(0, room);
  const notice =
    images.length > room
      ? `Attach up to ${FEEDBACK_SCREENSHOT_MAX_COUNT} screenshots.`
      : images.length < files.length
        ? "Only images can be attached as screenshots."
        : null;
  return { accepted, notice };
}

/** What a created report adds when GitHub would not take some of its screenshots (ADR-0137). */
export function describeDroppedScreenshots(result: FeedbackSubmitResult): string | null {
  if (result.status !== "created" || result.screenshotsDropped === 0) return null;
  return result.screenshotsDropped === 1
    ? "1 screenshot could not be attached. Your report was sent without it."
    : `${result.screenshotsDropped} screenshots could not be attached. Your report was sent without them.`;
}

/**
 * Whether the Feedback form itself can be submitted, as opposed to whether the page is offered.
 *
 * `signed-in` now means the credential is believed good rather than merely present, so a saved
 * account whose token the identity service has refused is not submittable. Offering the form to
 * one is how the app came to tell a signed-in user to sign in (#1008).
 */
export function canSubmitFeedback(account: ConnectAccountStatus | null): boolean {
  return account?.state === "signed-in";
}

/** What to say in place of the form, which differs for a session that ran out. */
export function describeFeedbackSignInPrompt(account: ConnectAccountStatus | null): {
  readonly title: string;
  readonly description: string;
} {
  if (account?.state === "needs-sign-in") {
    return {
      title: "Sign in again to send feedback",
      description:
        "Your AFAR account session has expired. Sign in again under Connections, then come back — what you typed is kept until you send it or reload.",
    };
  }
  return {
    title: "Sign in to send feedback",
    description:
      "Reports are sent under your AFAR account so we can reply to you by email. Sign in under Connections, then come back.",
  };
}

/**
 * Whether a result means the panel's idea of the account is stale.
 *
 * Only a refusal about the credential does. The panel re-reads its status on one, so it stops
 * presenting itself as signed in while showing a sign-in demand — the two states the reporter saw
 * at once (#1008).
 */
export function feedbackFailureInvalidatesAccount(result: FeedbackSubmitResult): boolean {
  return result.status === "failed" && result.reason === "sign-in-required";
}

export function isClientLocalSettingsPath(
  connectBridgeAvailable: boolean,
  pathname: string,
): boolean {
  return (
    pathname === "/settings/connections" ||
    pathname === "/settings/appearance" ||
    pathname === "/settings/integrations" ||
    (connectBridgeAvailable &&
      (pathname === "/settings/admin" ||
        pathname === "/settings/account" ||
        pathname === "/settings/updates" ||
        pathname === "/settings/feedback" ||
        pathname === "/settings/network" ||
        pathname === "/settings/organizations"))
  );
}

/**
 * How many elements of the form a file drag is currently inside. Crossing into a child fires
 * `dragleave` on the parent after `dragenter` on the child, so a boolean would flicker; a depth
 * stays above zero until the drag has left the form entirely. The drop zone is highlighted while
 * the depth is positive.
 */
export function nextFeedbackDragDepth(depth: number, event: "enter" | "leave" | "drop"): number {
  if (event === "drop") return 0;
  return event === "enter" ? depth + 1 : Math.max(0, depth - 1);
}
